import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BadgePercent,
  Check,
  DoorClosed,
  DoorOpen,
  Plus,
  Receipt,
  Ticket,
  Trash2,
  X,
} from 'lucide-react';
import {
  brichAb,
  createGutschein,
  deleteGutschein,
  fetchBestellungen,
  fetchGutscheine,
  fetchShopEinstellungen,
  fetchShopZahlen,
  schalteFrei,
  setzeShopEinstellungen,
  updateGutschein,
  type BestellStatus,
  type Gutschein,
  type ShopEinstellungen,
} from '@/api/shop.api';
import { fetchTarife } from '@/api/plans.api';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { datum, geld, zahl, zeitpunkt } from '@/components/format';

// ─── Shop-Verwaltung ─────────────────────────────────────────────────────────
//
// Alles, was am Verkauf eingestellt wird, an einem Ort: ob überhaupt jemand ein
// Konto anlegen darf, ob gerade eine Aktion läuft, welche Gutscheincodes es
// gibt und welche Bestellungen auf eine Entscheidung warten.
//
// Die PREISE stehen bewusst nicht hier, sondern bei den Tarifen: ein Preis
// gehört zum Tarif. Zwei Stellen, an denen man ihn ändern kann, wären eine zu
// viel.

const SCHLUESSEL = ['admin', 'shop'] as const;

// ─── Zahlen ──────────────────────────────────────────────────────────────────

function Kachel({ titel, wert, ton }: { titel: string; wert: string; ton?: 'gruen' | 'gelb' }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      {/* break-words und schmalere Laufweite am Telefon: „FREIGESCHALTET" ist
          ein einziges Wort und ragte auf 320 px aus der Kachel heraus. */}
      <p className="break-words text-xs font-medium uppercase text-slate-400 sm:tracking-wide">
        {titel}
      </p>
      <p
        className={`mt-1 text-xl font-semibold tabular-nums sm:text-2xl ${
          ton === 'gruen' ? 'text-emerald-600' : ton === 'gelb' ? 'text-amber-600' : 'text-slate-900'
        }`}
      >
        {wert}
      </p>
    </div>
  );
}

function Zahlen() {
  const { data, isLoading } = useQuery({
    queryKey: [...SCHLUESSEL, 'zahlen'],
    queryFn: fetchShopZahlen,
  });

  if (isLoading || !data) {
    return <p className="text-sm text-slate-400">Zahlen werden geladen…</p>;
  }

  const hoechsteWoche = Math.max(1, ...data.neueKonten.map((w) => w.anzahl));

  return (
    <section className="space-y-4">
      {/* Schon am Telefon zwei Spalten: vier volle Kacheln untereinander
          schöben alles Übrige aus dem Bild. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {/* „Eingenommen" statt „Umsatz freigeschaltet": das lange Wort ließ
            sich auf 320 px nicht mehr umbrechen und wurde mitten durchgetrennt.
            Gemeint ist dasselbe – der Betrag aus freigeschalteten Bestellungen. */}
        <Kachel titel="Eingenommen" wert={geld(data.umsatzCents)} ton="gruen" />
        <Kachel titel="Offen" wert={geld(data.offenCents)} ton={data.offenCents > 0 ? 'gelb' : undefined} />
        <Kachel titel="Bestellungen offen" wert={zahl(data.bestellungen.offen)} />
        <Kachel
          titel="Gutscheine aktiv"
          wert={`${zahl(data.gutscheine.aktiv)} · ${zahl(data.gutscheine.eingeloest)} eingelöst`}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-slate-900">Neue Konten je Woche</h3>
          {data.neueKonten.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Noch keine Anmeldungen.</p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {data.neueKonten.map((w) => (
                <li key={w.woche} className="flex items-center gap-3 text-sm">
                  <span className="w-24 shrink-0 text-xs tabular-nums text-slate-500">{w.woche}</span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <span
                      className="block h-full rounded-full bg-blue-500"
                      style={{ width: `${(w.anzahl / hoechsteWoche) * 100}%` }}
                    />
                  </span>
                  <span className="w-8 shrink-0 text-right text-xs tabular-nums text-slate-600">
                    {w.anzahl}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-slate-900">Konten je Tarif</h3>
          {data.jeTarif.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Keine Zuordnung.</p>
          ) : (
            <ul className="mt-3 divide-y divide-slate-100">
              {data.jeTarif.map((t) => (
                <li key={t.name} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-slate-700">{t.name}</span>
                  <span className="tabular-nums text-slate-500">{zahl(t.anzahl)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Registrierung und Aktion ────────────────────────────────────────────────

function Einstellungen() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: [...SCHLUESSEL, 'einstellungen'],
    queryFn: fetchShopEinstellungen,
  });

  const [entwurf, setEntwurf] = useState<ShopEinstellungen | null>(null);

  // Der Entwurf folgt dem Server nur, solange nichts Eigenes darin steht –
  // sonst überschriebe ein Hintergrund-Abgleich, was gerade getippt wird.
  useEffect(() => {
    if (data && !entwurf) setEntwurf(data);
  }, [data, entwurf]);

  const speichern = useMutation({
    mutationFn: setzeShopEinstellungen,
    onSuccess: (neu) => {
      setEntwurf(neu);
      queryClient.setQueryData([...SCHLUESSEL, 'einstellungen'], neu);
      queryClient.invalidateQueries({ queryKey: [...SCHLUESSEL, 'zahlen'] });
      toast.success('Gespeichert.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!entwurf) return <p className="text-sm text-slate-400">Wird geladen…</p>;

  const feld = <K extends keyof ShopEinstellungen>(k: K, v: ShopEinstellungen[K]) =>
    setEntwurf({ ...entwurf, [k]: v });

  const geaendert = !!data && JSON.stringify(data) !== JSON.stringify(entwurf);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* ─── Registrierung ─── */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          {entwurf.registrierungOffen ? (
            <DoorOpen className="h-4 w-4 text-emerald-500" aria-hidden="true" />
          ) : (
            <DoorClosed className="h-4 w-4 text-amber-500" aria-hidden="true" />
          )}
          Registrierung
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Ist sie zu, weist der Shop neue Anmeldungen ab – bestehende Konten sind
          nicht betroffen und melden sich weiter an.
        </p>

        <label className="mt-4 flex items-start gap-3">
          <input
            type="checkbox"
            checked={entwurf.registrierungOffen}
            onChange={(e) => feld('registrierungOffen', e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600"
          />
          <span className="text-sm text-slate-700">
            Neue Konten dürfen angelegt werden
            {!entwurf.registrierungOffen && (
              <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                gesperrt
              </span>
            )}
          </span>
        </label>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-700">Text bei gesperrter Anmeldung</span>
          <textarea
            rows={3}
            value={entwurf.registrierungHinweis}
            onChange={(e) => feld('registrierungHinweis', e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            placeholder="Wir nehmen gerade keine neuen Kunden auf."
          />
          <span className="mt-1 block text-xs text-slate-400">
            Steht so im Shop. Ein Satz, warum – und wann es wieder losgeht – erspart
            Rückfragen per E-Mail.
          </span>
        </label>
      </section>

      {/* ─── Rabattaktion ─── */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <BadgePercent className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Rabattaktion
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Gilt für alle Tarife im Shop. Legt jemand zusätzlich einen Gutschein vor,
          zählt der größere von beiden – gestapelt wird nicht.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Nachlass in Prozent</span>
            <input
              type="number"
              min={0}
              max={100}
              value={entwurf.aktionProzent}
              onChange={(e) => feld('aktionProzent', Number(e.target.value) || 0)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums"
            />
            <span className="mt-1 block text-xs text-slate-400">0 = keine Aktion.</span>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Läuft bis</span>
            <input
              type="date"
              value={entwurf.aktionBis}
              onChange={(e) => feld('aktionBis', e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
            <span className="mt-1 block text-xs text-slate-400">
              Der Tag zählt noch mit. Leer = ohne Ende.
            </span>
          </label>
        </div>

        <label className="mt-3 block">
          <span className="text-sm font-medium text-slate-700">Name der Aktion</span>
          <input
            value={entwurf.aktionTitel}
            onChange={(e) => feld('aktionTitel', e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            placeholder="Sommeraktion"
          />
          <span className="mt-1 block text-xs text-slate-400">
            Steht im Shop am Preis und später in jeder Bestellung – so bleibt
            nachvollziehbar, warum jemand weniger gezahlt hat.
          </span>
        </label>

        {entwurf.aktionProzent > 0 && (
          <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            Ein Tarif für 15,00 € kostet damit {geld(Math.round(1500 * (1 - entwurf.aktionProzent / 100)))}
            {entwurf.aktionBis ? ` – bis einschließlich ${datum(entwurf.aktionBis)}.` : '.'}
          </p>
        )}
      </section>

      <div className="flex flex-col gap-2 sm:flex-row lg:col-span-2">
        <button
          onClick={() => speichern.mutate(entwurf)}
          disabled={!geaendert || speichern.isPending}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
        >
          {speichern.isPending ? 'Wird gespeichert…' : 'Änderungen speichern'}
        </button>
        {geaendert && (
          <button
            onClick={() => data && setEntwurf(data)}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Verwerfen
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Bestellungen ────────────────────────────────────────────────────────────

const STATUS_STIL: Record<BestellStatus, string> = {
  offen: 'bg-amber-50 text-amber-700',
  freigeschaltet: 'bg-emerald-50 text-emerald-700',
  abgebrochen: 'bg-slate-100 text-slate-500',
};

function Bestellungen() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [filter, setFilter] = useState<BestellStatus | 'alle'>('offen');

  const { data, isLoading } = useQuery({
    queryKey: [...SCHLUESSEL, 'bestellungen', filter],
    queryFn: () => fetchBestellungen(filter === 'alle' ? undefined : filter),
  });

  const aktualisieren = () => {
    queryClient.invalidateQueries({ queryKey: [...SCHLUESSEL, 'bestellungen'] });
    queryClient.invalidateQueries({ queryKey: [...SCHLUESSEL, 'zahlen'] });
    queryClient.invalidateQueries({ queryKey: [...SCHLUESSEL, 'gutscheine'] });
  };

  const frei = useMutation({
    mutationFn: (id: string) => schalteFrei(id),
    onSuccess: (r) => {
      aktualisieren();
      toast.success(r.message);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const ab = useMutation({
    mutationFn: (id: string) => brichAb(id),
    onSuccess: (r) => {
      aktualisieren();
      toast.success(r.message);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const freischalten = async (id: string, email: string, tarif: string, betrag: string) => {
    const ok = await confirm({
      title: 'Bestellung freischalten?',
      message:
        `${email} bekommt den Tarif „${tarif}". Prüfen Sie vorher, dass ${betrag} ` +
        'tatsächlich eingegangen sind – die Zahlung selbst läuft außerhalb dieser Anwendung.',
      confirmLabel: 'Freischalten',
    });
    if (ok) frei.mutate(id);
  };

  const abbrechen = async (id: string, email: string) => {
    const ok = await confirm({
      title: 'Bestellung abbrechen?',
      message:
        `Die Bestellung von ${email} wird abgebrochen. Der Tarif bleibt, wie er ist. ` +
        'Ein eingelöster Gutschein wird wieder frei.',
      confirmLabel: 'Abbrechen',
      variant: 'danger',
    });
    if (ok) ab.mutate(id);
  };

  const zeilen = data ?? [];

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Receipt className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Bestellungen
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            Eine Bestellung schaltet den Tarif nicht selbst frei – das geschieht hier
            von Hand, nachdem die Zahlung eingegangen ist. Der Betrag ist beim
            Bestellen eingefroren; eine spätere Preisänderung schreibt ihn nicht um.
          </p>
        </div>
        {/* flex-wrap statt shrink-0: auf schmalen Geräten bricht die Leiste um,
            statt vier Schalter auf Briefmarkengröße zu drücken. */}
        <div className="flex w-full flex-wrap gap-1 rounded-lg bg-slate-100 p-1 sm:w-auto">
          {(['offen', 'freigeschaltet', 'abgebrochen', 'alle'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize ${
                filter === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p className="mt-4 text-sm text-slate-400">Wird geladen…</p>}

      {!isLoading && zeilen.length === 0 && (
        <p className="mt-4 text-sm text-slate-400">
          {filter === 'offen' ? 'Nichts wartet auf eine Entscheidung.' : 'Keine Einträge.'}
        </p>
      )}

      {/* Karten bis xl, danach die Tabelle. Die Umschaltung liegt so spät, weil
          die Tabelle gemessen 808 px braucht: bei 1024 px stehen neben der
          Navigation nur 694 zur Verfügung, und dann ist der Knopf
          „Freischalten" rechts abgeschnitten – man entschiede über etwas, das
          man nicht ganz sieht. Eine Karte wird nie abgeschnitten. */}
      {zeilen.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-100 xl:hidden">
          {zeilen.map((b) => (
            <li key={b.id} className="space-y-2 py-3">
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 break-all text-sm text-slate-800">
                  {b.user?.email ?? '– gelöscht –'}
                </span>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STIL[b.status]}`}
                >
                  {b.status}
                </span>
              </div>

              <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                <span className="text-slate-600">
                  {b.plan?.name ?? '–'}
                  {b.plan?.interval === 'year' && (
                    <span className="ml-1 text-xs text-slate-400">/Jahr</span>
                  )}
                  {b.plan?.interval === 'month' && (
                    <span className="ml-1 text-xs text-slate-400">/Monat</span>
                  )}
                </span>
                <span className="font-medium tabular-nums text-slate-800">
                  {geld(b.betragCents, b.currency)}
                </span>
                {b.rabattCents > 0 && (
                  <span className="text-xs text-slate-400 line-through">
                    {geld(b.listenpreisCents, b.currency)}
                  </span>
                )}
              </div>

              <p className="text-xs text-slate-500">
                {b.rabattGrund ?? 'ohne Rabatt'}
                {/* Der Code nur dann als Merkmal, wenn der Grund ihn NICHT schon
                    nennt – also wenn eine Aktion gewonnen hat und der Gutschein
                    trotzdem eingelöst wurde. Genau der Fall ist erklärungsbedürftig. */}
                {b.coupon && !b.rabattGrund?.includes(b.coupon.code) && (
                  <span className="ml-1 rounded bg-slate-100 px-1 py-0.5 font-mono">
                    {b.coupon.code}
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-400">
                {zeitpunkt(b.createdAt)}
                {b.bearbeitetVon && ` · ${b.bearbeitetVon}, ${zeitpunkt(b.bearbeitetAm)}`}
              </p>

              {b.status === 'offen' && (
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() =>
                      freischalten(
                        b.id,
                        b.user?.email ?? 'das Konto',
                        b.plan?.name ?? '–',
                        geld(b.betragCents, b.currency),
                      )
                    }
                    disabled={frei.isPending}
                    className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-emerald-600 px-2.5 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    Freischalten
                  </button>
                  <button
                    onClick={() => abbrechen(b.id, b.user?.email ?? 'das Konto')}
                    disabled={ab.isPending}
                    className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-slate-300 px-2.5 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                    Abbrechen
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {zeilen.length > 0 && (
        <div className="mt-4 hidden overflow-x-auto xl:block">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                {/* pr-3 wie in den Datenzellen: ohne das stoßen „Betrag" und
                    „Rabatt" aneinander, weil das eine rechts, das andere links
                    ausgerichtet ist. */}
                <th className="pb-2 pr-3 font-medium">Konto</th>
                <th className="pb-2 pr-3 font-medium">Tarif</th>
                <th className="pb-2 pr-3 text-right font-medium">Betrag</th>
                <th className="pb-2 pr-3 font-medium">Rabatt</th>
                <th className="pb-2 pr-3 font-medium">Eingegangen</th>
                <th className="pb-2 pr-3 font-medium">Stand</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {zeilen.map((b) => (
                <tr key={b.id} className="align-top">
                  <td className="py-2.5 pr-3">
                    <span className="block text-slate-800">{b.user?.email ?? '– gelöscht –'}</span>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-600">
                    {b.plan?.name ?? '–'}
                    {b.plan?.interval === 'year' && (
                      <span className="ml-1 text-xs text-slate-400">/Jahr</span>
                    )}
                    {b.plan?.interval === 'month' && (
                      <span className="ml-1 text-xs text-slate-400">/Monat</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-slate-800">
                    {geld(b.betragCents, b.currency)}
                    {b.rabattCents > 0 && (
                      <span className="ml-1 text-xs text-slate-400 line-through">
                        {geld(b.listenpreisCents, b.currency)}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-xs text-slate-500">
                    {b.rabattGrund ?? '–'}
                    {b.coupon && !b.rabattGrund?.includes(b.coupon.code) && (
                      <span className="ml-1 rounded bg-slate-100 px-1 py-0.5 font-mono">
                        {b.coupon.code}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-xs text-slate-500">{zeitpunkt(b.createdAt)}</td>
                  <td className="py-2.5 pr-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STIL[b.status]}`}
                    >
                      {b.status}
                    </span>
                    {b.bearbeitetVon && (
                      <span className="mt-1 block text-xs text-slate-400">
                        {b.bearbeitetVon}, {zeitpunkt(b.bearbeitetAm)}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 text-right">
                    {b.status === 'offen' && (
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() =>
                            freischalten(
                              b.id,
                              b.user?.email ?? 'das Konto',
                              b.plan?.name ?? '–',
                              geld(b.betragCents, b.currency),
                            )
                          }
                          disabled={frei.isPending}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <Check className="h-3.5 w-3.5" aria-hidden="true" />
                          Freischalten
                        </button>
                        <button
                          onClick={() => abbrechen(b.id, b.user?.email ?? 'das Konto')}
                          disabled={ab.isPending}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                        >
                          <X className="h-3.5 w-3.5" aria-hidden="true" />
                          Abbrechen
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─── Gutscheine ──────────────────────────────────────────────────────────────

const LEER = {
  code: '',
  beschreibung: '',
  art: 'prozent' as 'prozent' | 'betrag',
  wert: '',
  gueltigBis: '',
  maxEinloesungen: '',
  nurFuerPlanIds: [] as string[],
};

function Gutscheine() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [formularOffen, setFormularOffen] = useState(false);
  const [neu, setNeu] = useState(LEER);

  const { data, isLoading } = useQuery({
    queryKey: [...SCHLUESSEL, 'gutscheine'],
    queryFn: fetchGutscheine,
  });
  const { data: tarife } = useQuery({ queryKey: ['admin', 'plans'], queryFn: fetchTarife });

  const aktualisieren = () =>
    queryClient.invalidateQueries({ queryKey: [...SCHLUESSEL, 'gutscheine'] });

  const anlegen = useMutation({
    mutationFn: createGutschein,
    onSuccess: (g) => {
      aktualisieren();
      setNeu(LEER);
      setFormularOffen(false);
      toast.success(`„${g.code}" ist einlösbar.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const umschalten = useMutation({
    mutationFn: ({ id, aktiv }: { id: string; aktiv: boolean }) => updateGutschein(id, { aktiv }),
    onSuccess: (g) => {
      aktualisieren();
      toast.success(g.aktiv ? `„${g.code}" ist wieder einlösbar.` : `„${g.code}" ist stillgelegt.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const loeschen = useMutation({
    mutationFn: deleteGutschein,
    onSuccess: (r) => {
      aktualisieren();
      toast.success(r.message);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const absenden = () => {
    const wert = Number(neu.wert);
    if (!Number.isFinite(wert) || wert <= 0) {
      toast.error('Der Nachlass braucht einen Wert größer null.');
      return;
    }
    anlegen.mutate({
      code: neu.code.trim().toUpperCase(),
      beschreibung: neu.beschreibung.trim() || null,
      prozent: neu.art === 'prozent' ? Math.round(wert) : null,
      // Eingabe in Euro, gespeichert in Cent – so steht in der Maske, was
      // draufsteht, und in der Datenbank, was gerechnet wird.
      betragCents: neu.art === 'betrag' ? Math.round(wert * 100) : null,
      gueltigBis: neu.gueltigBis || null,
      maxEinloesungen: neu.maxEinloesungen ? Number(neu.maxEinloesungen) : null,
      nurFuerPlanIds: neu.nurFuerPlanIds,
    });
  };

  const entfernen = async (g: Gutschein) => {
    const benutzt = g._count.orders > 0;
    const ok = await confirm({
      title: benutzt ? `„${g.code}" stilllegen?` : `„${g.code}" löschen?`,
      message: benutzt
        ? `Der Code steckt in ${g._count.orders} Bestellung(en) und wird deshalb nur ` +
          'stillgelegt, nicht gelöscht – sonst stünde dort ein Rabatt ohne Herkunft. ' +
          'Einlösen kann ihn danach niemand mehr.'
        : 'Der Code wurde noch nie eingelöst und verschwindet vollständig.',
      confirmLabel: benutzt ? 'Stilllegen' : 'Löschen',
      variant: 'danger',
    });
    if (ok) loeschen.mutate(g.id);
  };

  const zeilen = data ?? [];

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Ticket className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Gutscheincodes
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            Ein Code wirkt nur, wenn er besser ist als eine laufende Aktion. Der Code
            selbst lässt sich später nicht ändern – wer ihn schon hat, stünde sonst
            ohne da.
          </p>
        </div>
        <button
          onClick={() => setFormularOffen((o) => !o)}
          className="inline-flex w-full shrink-0 items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 sm:w-auto"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Code anlegen
        </button>
      </div>

      {formularOffen && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Code</span>
              <input
                value={neu.code}
                onChange={(e) => setNeu({ ...neu, code: e.target.value.toUpperCase() })}
                placeholder="SOMMER25"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase"
              />
              <span className="mt-1 block text-xs text-slate-400">
                Buchstaben, Ziffern, Bindestrich – etwas, das man am Telefon durchgeben kann.
              </span>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Art des Nachlasses</span>
              <select
                value={neu.art}
                onChange={(e) => setNeu({ ...neu, art: e.target.value as 'prozent' | 'betrag' })}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="prozent">Prozent</option>
                <option value="betrag">Fester Betrag</option>
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">
                {neu.art === 'prozent' ? 'Prozent' : 'Betrag in Euro'}
              </span>
              <input
                type="number"
                min={neu.art === 'prozent' ? 1 : 0.01}
                max={neu.art === 'prozent' ? 100 : undefined}
                step={neu.art === 'prozent' ? 1 : 0.01}
                value={neu.wert}
                onChange={(e) => setNeu({ ...neu, wert: e.target.value })}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Gültig bis</span>
              <input
                type="date"
                value={neu.gueltigBis}
                onChange={(e) => setNeu({ ...neu, gueltigBis: e.target.value })}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-slate-400">Leer = ohne Ende.</span>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Höchstens … Einlösungen</span>
              <input
                type="number"
                min={1}
                value={neu.maxEinloesungen}
                onChange={(e) => setNeu({ ...neu, maxEinloesungen: e.target.value })}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums"
              />
              <span className="mt-1 block text-xs text-slate-400">Leer = unbegrenzt.</span>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Nur für diese Tarife</span>
              <select
                multiple
                size={3}
                value={neu.nurFuerPlanIds}
                onChange={(e) =>
                  setNeu({
                    ...neu,
                    nurFuerPlanIds: Array.from(e.target.selectedOptions, (o) => o.value),
                  })
                }
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                {(tarife?.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-slate-400">
                Nichts gewählt = gilt für alle.
              </span>
            </label>

            <label className="block sm:col-span-2 lg:col-span-3">
              <span className="text-sm font-medium text-slate-700">Wofür ist der Code?</span>
              <input
                value={neu.beschreibung}
                onChange={(e) => setNeu({ ...neu, beschreibung: e.target.value })}
                placeholder="Messe Hannover, Handzettel"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-slate-400">
                Nur für die Konsole. In einem halben Jahr weiß sonst niemand mehr, wozu
                es den Code gab.
              </span>
            </label>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button
              onClick={absenden}
              disabled={!neu.code.trim() || !neu.wert || anlegen.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
            >
              {anlegen.isPending ? 'Wird angelegt…' : 'Anlegen'}
            </button>
            <button
              onClick={() => {
                setNeu(LEER);
                setFormularOffen(false);
              }}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {isLoading && <p className="mt-4 text-sm text-slate-400">Wird geladen…</p>}

      {!isLoading && zeilen.length === 0 && (
        <p className="mt-4 text-sm text-slate-400">Noch kein Code angelegt.</p>
      )}

      {zeilen.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-100">
          {zeilen.map((g) => {
            const aufgebraucht =
              g.maxEinloesungen !== null && g.einloesungen >= g.maxEinloesungen;
            const abgelaufen = !!g.gueltigBis && new Date(g.gueltigBis) < new Date();
            return (
              // Am Telefon gestapelt, ab sm eine Zeile. Vorher stand alles in
              // EINER Zeile mit lauter shrink-0 – auf 375 px blieb von der
              // Beschreibung „Mess…" übrig und die Merkmale rutschten um.
              <li key={g.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-3">
                {/* Code, Wert und Zustand – gehören zusammen und bleiben zusammen */}
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded bg-slate-100 px-2 py-1 font-mono text-sm ${
                      g.aktiv && !aufgebraucht && !abgelaufen
                        ? 'text-slate-800'
                        : 'text-slate-400 line-through'
                    }`}
                  >
                    {g.code}
                  </span>
                  <span className="text-sm font-medium text-slate-700">
                    {g.prozent !== null ? `${g.prozent} %` : geld(g.betragCents)}
                  </span>
                  {!g.aktiv && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      stillgelegt
                    </span>
                  )}
                  {g.aktiv && aufgebraucht && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                      aufgebraucht
                    </span>
                  )}
                  {g.aktiv && !aufgebraucht && abgelaufen && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                      abgelaufen
                    </span>
                  )}
                </div>

                {/* Wozu und wie oft – zwei Zeilen statt vier Spalten */}
                <div className="min-w-0 flex-1 sm:pt-1">
                  <p className="truncate text-xs text-slate-500">
                    {g.beschreibung || '—'}
                    {g.nurFuerPlanIds.length > 0 && (
                      <span className="ml-2 text-slate-400">
                        nur{' '}
                        {g.nurFuerPlanIds
                          .map((id) => tarife?.data.find((t) => t.id === id)?.name ?? '?')
                          .join(', ')}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-400">
                    {zahl(g.einloesungen)}
                    {g.maxEinloesungen !== null ? ` / ${zahl(g.maxEinloesungen)}` : ''} eingelöst
                    {' · '}
                    {g.gueltigBis ? `bis ${datum(g.gueltigBis)}` : 'ohne Ende'}
                  </p>
                </div>

                <div className="flex shrink-0 gap-1">
                  <button
                    onClick={() => umschalten.mutate({ id: g.id, aktiv: !g.aktiv })}
                    disabled={umschalten.isPending}
                    className="rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {g.aktiv ? 'Stilllegen' : 'Wieder freigeben'}
                  </button>
                  <button
                    onClick={() => entfernen(g)}
                    disabled={loeschen.isPending}
                    className="rounded-md border border-slate-300 p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    aria-label={`${g.code} entfernen`}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ─── Seite ───────────────────────────────────────────────────────────────────

export function ShopPage() {
  return (
    // p-4 sm:p-6 wie überall in der Konsole – ohne das kleben Text und Karten
    // am Bildschirmrand, was am Telefon sofort auffällt.
    <div className="space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Shop</h1>
        <p className="mt-1 max-w-3xl text-sm leading-relaxed text-slate-600">
          Registrierung, Rabatte und Bestellungen. Die Preise selbst stehen bei den{' '}
          {/* Link statt <a>: ein voller Seitenwechsel würde die Sitzung verlieren –
              das Zugangstoken liegt nur im Speicher. */}
          <Link to="/tarife" className="text-blue-600 underline underline-offset-2">
            Tarifen
          </Link>{' '}
          – dort wird auch entschieden, welcher Tarif im Shop überhaupt auftaucht.
        </p>
      </header>

      <Zahlen />
      <Einstellungen />
      <Bestellungen />
      <Gutscheine />
    </div>
  );
}
